import { Injectable, computed, inject, signal } from '@angular/core';
import { AnalysisDetailStore } from '@features/analyses/store/analysis-detail.store';
import { type InvalidationRequest } from '@features/analyses/store/models/analysis-store.model';
import { AnalysisService } from '@shared/services/analysis.service';
import { ToastService } from '@shared/services/toast.service';
import { ExecutionPreflightService } from '@shared/services/execution-preflight';
import { CurrentOrganizationStore } from '@features/organizations/store/current-organization.store';
import { BackgroundExecutionService } from '@core/services/background-execution.service';
import { type TimelineActionExecution } from '@features/analyses/components/analysis-timeline/analysis-timeline';
import { type DebugTaskActionExecution } from '@features/analyses/components/analysis-detail-debug-panel/analysis-detail-debug-panel';
import {
  FIRST_PIPELINE_TASK,
  LAST_PIPELINE_TASK,
  getStageConfigForTask,
} from '@features/analyses/config/pipeline-stage.config';
import { environment } from '@env/environment';

const debugLog = (...args: unknown[]) => {
  if (!environment.production) {
    console.log(...args);
  }
};

/** Minimal action descriptor — replaces the removed server-driven PipelineAction */
export interface PipelineAction {
  action: string;
  targetStatus?: string;
  hasPermission?: boolean;
  disabled?: boolean;
  label?: string;
  description?: string;
  requiredPermission?: string;
}

export type PipelineActionExecution =
  | PipelineAction
  | TimelineActionExecution
  | DebugTaskActionExecution;

interface TaskExecutionRequest {
  startTask: number;
  endTask: number;
  notes?: string;
}

/**
 * Facade that encapsulates all SI pipeline workflow execution logic.
 *
 * Every path that dispatches tasks goes through ExecutionPreflightService first,
 * which asks for whatever selection the dispatched range needs (file, template)
 * and persists it on the SI analysis. To change when a dialog appears, edit
 * PIPELINE_STAGE_CONFIGS — not this file.
 */
@Injectable()
export class AnalysisWorkflowFacade {
  private readonly store = inject(AnalysisDetailStore);
  private readonly analysisService = inject(AnalysisService);
  private readonly toastService = inject(ToastService);
  private readonly preflight = inject(ExecutionPreflightService);
  private readonly currentOrgStore = inject(CurrentOrganizationStore);
  private readonly backgroundExecution = inject(BackgroundExecutionService);

  readonly isSyncing = signal(false);
  readonly isPipelineActionRunning = signal(false);
  private readonly requestedTaskWindow = signal<TaskExecutionRequest | null>(null);
  readonly activeTaskWindow = computed(() => {
    const analysis = this.analysis;
    const requested =
      this.requestedTaskWindow() ??
      (analysis
        ? this.backgroundExecution.taskWindowForAnalysis(analysis.analysisId)
        : null);
    if (!requested) return null;
    if (this.isPipelineActionRunning()) return requested;
    const status = analysis?.technicalStatus;
    const task = Number(status?.task ?? 0);
    if (status?.state === 'failed') return null;
    if (status?.state === 'completed' && task >= requested.endTask) return null;
    return requested;
  });

  /**
   * True while the pipeline has remaining tasks to run.
   *
   * Note: `technicalStatus.stage` is never populated by the backend
   * (toTechnicalStatus() only fills task/taskKey/state/totalTasks), so
   * completion must be derived from task vs. totalTasks instead of stage.
   */
  readonly canResume = computed(() => {
    const analysis = this.store.selectDetail();
    if (!analysis) return false;
    const status = analysis.technicalStatus;
    if (!status) return true;
    const isDone =
      status.task > LAST_PIPELINE_TASK ||
      (status.state === 'completed' && status.task >= LAST_PIPELINE_TASK);
    return !isDone;
  });

  private get analysis() {
    return this.store.selectDetail();
  }

  private get filesOverview() {
    return this.store.selectFilesOverview();
  }

  private get organizationId(): string | null {
    return (
      this.currentOrgStore.organization()?.id ||
      this.analysis?.organizationId ||
      null
    );
  }

  // ─── Public API ───────────────────────────────────────────────────────────

  readonly isResettingState = signal(false);

  async retryPlanning(planningId: string): Promise<void> {
    const orgId = this.organizationId;
    const analysis = this.analysis;
    if (!orgId || !analysis || this.isPipelineActionRunning()) return;

    this.isPipelineActionRunning.set(true);
    this.requestedTaskWindow.set({ startTask: 4, endTask: 5 });
    try {
      await this.analysisService.executeAnalysisTasks(orgId, analysis.analysisId, {
        start_task: 4,
        end_task: 5,
        planning_id: planningId,
        notes: 'Retry failed planning',
      });
      this.toastService.success('Planung wird erneut ausgeführt');
      this.ensureRealtimeStream();
      this.reload();
    } catch (error) {
      this.requestedTaskWindow.set(null);
      this.toastService.error(
        (error as Error)?.message || 'Planung konnte nicht erneut gestartet werden',
      );
    } finally {
      this.isPipelineActionRunning.set(false);
    }
  }

  async resetStuckState(): Promise<void> {
    const orgId = this.organizationId;
    const analysisId = this.analysis?.analysisId;

    if (!orgId || !analysisId) {
      this.toastService.error('Erforderliche Daten fehlen');
      return;
    }

    try {
      this.isResettingState.set(true);
      await this.analysisService.resetStuckState(orgId, analysisId);
      this.toastService.success(
        'Status zurückgesetzt — Analyse kann jetzt erneut gestartet werden.',
      );
      this.store.cascadeInvalidateSections({
        sections: ['detail'],
        reason: 'status_change',
      });
      this.store.reloadCurrentAnalysis(orgId);
    } catch (error) {
      const message =
        (error as Error)?.message || 'Status-Reset fehlgeschlagen';
      this.toastService.error(message);
    } finally {
      this.isResettingState.set(false);
    }
  }

  /**
   * Runs the pipeline from wherever it currently stands through to the last
   * task (6) — same idea as the campaign bulk-run "Ausführen" button
   * (the campaign execution setup): resolve every
   * selection the remaining range needs *up front*, then dispatch ONE
   * execute call across the whole window, instead of stopping at the first
   * stage that needs input.
   *
   * Two stages can require input — "analyzed" (media, tasks 2-3) and
   * "planned" (template, tasks 4-5) — and both can be missing at once (e.g.
   * resuming from task 2 with a brand new analysis). The preflight resolves
   * both in this same click, so one click always runs to the end.
   *
   * When the run starts at geocut the file being selected does not exist yet —
   * geocut is about to produce it — so the preflight asks for the *type* to
   * produce rather than a concrete file. That is why this no longer stops at
   * geocut and makes the user click again.
   */
  async resumeToCompletion(): Promise<void> {
    const orgId = this.organizationId;
    const analysis = this.analysis;

    if (!orgId || !analysis) {
      this.toastService.error('Erforderliche Daten fehlen');
      return;
    }
    if (this.isPipelineActionRunning()) return;

    const status = analysis.technicalStatus;
    const currentTask = Number(status?.task ?? 0);
    const startTask = this.resolveResumeStartTask(currentTask, status?.state);

    const endTask = LAST_PIPELINE_TASK;
    const requestedWindow = { startTask, endTask };

    this.isPipelineActionRunning.set(true);
    this.requestedTaskWindow.set(requestedWindow);
    try {
      const confirmed = await this.confirmSelectionsFor(requestedWindow);
      if (!confirmed) {
        this.requestedTaskWindow.set(null);
        return;
      }

      await this.executeTaskRange(orgId, analysis.analysisId, {
        startTask,
        endTask,
        notes: `Resume to completion from UI (${startTask}-${endTask})`,
      });
      this.toastService.success('Analyse wird fortgesetzt');
      this.ensureRealtimeStream();
      this.reload();
    } catch (error) {
      this.requestedTaskWindow.set(null);
      debugLog('[AnalysisWorkflowFacade] Failed to resume analysis', error);
      const message =
        (error as Error)?.message || 'Analyse konnte nicht fortgesetzt werden';
      this.toastService.error(message);
    } finally {
      this.isPipelineActionRunning.set(false);
    }
  }

  /**
   * Where resumeToCompletion() should (re)start:
   * - completed: move on to the next task.
   * - failed: rerun the whole stage the failed task belongs to, not just
   *   that task (e.g. a failed "planning" retries max_placement + planning).
   * - anything else (queued/processing/unknown): resume at the current task.
   */
  private resolveResumeStartTask(
    currentTask: number,
    state: string | undefined,
  ): number {
    if (state === 'completed') return currentTask + 1;
    if (state === 'failed') {
      const failedStage = getStageConfigForTask(currentTask);
      return failedStage ? failedStage.tasks[0] : Math.max(currentTask, 1);
    }
    return Math.max(currentTask, 1);
  }

  async syncAnalysis(): Promise<void> {
    const orgId = this.organizationId;
    const analysisId = this.analysis?.analysisId;

    if (!orgId || !analysisId) {
      this.toastService.error('Erforderliche Daten fehlen');
      return;
    }

    try {
      this.isSyncing.set(true);
      const freshAnalysis = await this.analysisService.fetchAnalysisDetail(
        orgId,
        analysisId,
        { sync: true },
      );
      this.store.updateAnalysisOptimistically(freshAnalysis);
      this.toastService.success('Analyse synchronisiert');
    } catch (error) {
      debugLog('[AnalysisWorkflowFacade] Sync failed:', error);
      this.toastService.error('Synchronisierung fehlgeschlagen');
    } finally {
      this.isSyncing.set(false);
    }
  }

  async onTimelineActionExecuted(
    actionPayload: PipelineActionExecution,
  ): Promise<void> {
    if (this.isPipelineActionRunning()) {
      return;
    }

    const { action, trigger, taskNo, startTask, endTask } =
      this.extractActionExecution(actionPayload);
    const actionName = action.action.replaceAll('_', '-');

    // Full redo/reset without a specific task context → reset + file dialog
    const isFullRedoOrReset =
      (actionName === 'redo-analysis' || actionName === 'reset-analysis') &&
      trigger !== 'rerun' &&
      !Number.isFinite(taskNo);

    if (isFullRedoOrReset) {
      return this.runRedoAnalysis();
    }

    // Resolve the window the same way executePipelineAction will, so the
    // preflight asks for exactly what the dispatched range needs — no more.
    const taskExecution = this.resolveTaskExecutionRequest({
      actionName,
      trigger,
      taskNo,
      startTask,
      endTask,
    });

    try {
      this.isPipelineActionRunning.set(true);
      if (taskExecution) this.requestedTaskWindow.set(taskExecution);

      if (taskExecution && !(await this.confirmSelectionsFor(taskExecution))) {
        this.requestedTaskWindow.set(null);
        return;
      }

      await this.executePipelineAction(actionPayload);
      this.emitSuccessToast(action, trigger, taskNo, startTask, endTask);
      if (actionName === 'cancel-analysis') {
        this.requestedTaskWindow.set(null);
        this.store.stopPolling();
        this.reload();
        return;
      }
      this.ensureRealtimeStream();
      this.reload();
    } catch (error) {
      this.requestedTaskWindow.set(null);
      debugLog(
        '[AnalysisWorkflowFacade] Failed to execute timeline action',
        error,
      );
      const message =
        (error as Error)?.message ||
        'Timeline-Aktion konnte nicht ausgeführt werden';
      this.toastService.error(message);
    } finally {
      this.isPipelineActionRunning.set(false);
    }
  }

  // ─── Private helpers ──────────────────────────────────────────────────────

  /** Ask for every selection the dispatched range needs. False → do not execute. */
  private async confirmSelectionsFor(taskWindow: {
    startTask: number;
    endTask: number;
  }): Promise<boolean> {
    const analysis = this.analysis;
    const orgId = this.organizationId;
    if (!analysis || !orgId) {
      this.toastService.error('Erforderliche Daten fehlen');
      return false;
    }
    return this.preflight.confirm(
      { ...analysis, plannings: this.store.selectPlannings() },
      orgId,
      taskWindow,
      this.filesOverview || undefined,
    );
  }

  private reload(): void {
    const orgId = this.organizationId;
    if (!orgId) return;
    // Invalidate the frontend cache so the reload fetches fresh data from the server
    this.store.cascadeInvalidateSections({
      sections: ['detail', 'files', 'plannings'],
      reason: 'workflow_advance',
    });
    this.store.reloadCurrentAnalysis(orgId);
  }

  private ensureRealtimeStream(): void {
    const analysis = this.analysis;
    const orgId = this.organizationId || analysis?.organizationId;
    const analysisId = analysis?.analysisId;
    if (!orgId || !analysisId) return;
    this.store.startPolling({ organizationId: orgId, analysisId });
    // Register for background notification so the user is informed even after
    // navigating away from this analysis detail page.
    this.backgroundExecution.watchAnalysis({
      analysisId,
      organizationId: orgId,
      orgSlug: this.currentOrgStore.organization()?.slug ?? null,
      taskWindow: this.requestedTaskWindow() ?? undefined,
    });
  }

  private emitSuccessToast(
    action: PipelineAction,
    trigger: string,
    taskNo?: number,
    startTask?: number,
    endTask?: number,
  ): void {
    if (
      trigger === 'rerun' &&
      Number.isFinite(taskNo) &&
      !!taskNo &&
      ['start-analysis', 'redo-analysis', 'reset-analysis'].includes(
        action.action.replaceAll('_', '-'),
      )
    ) {
      this.toastService.success('Analyse-Schritt wurde neu gestartet');
    } else if (
      trigger === 'start' &&
      action.action === 'start-analysis' &&
      Number.isFinite(startTask) &&
      Number.isFinite(endTask)
    ) {
      this.toastService.success('Analyse-Schritt wurde gestartet');
    } else if (action.action === 'start-analysis') {
      this.toastService.success('Analyse wurde gestartet');
    } else if (action.action === 'start-planning') {
      this.toastService.success('Planung wurde gestartet');
    } else if (action.action.replaceAll('_', '-') === 'cancel-analysis') {
      this.toastService.success('Ausführung wurde gestoppt');
    } else if (
      action.action === 'redo-analysis' ||
      action.action === 'reset-analysis'
    ) {
      this.toastService.success('Analyse wurde neu gestartet');
    } else {
      this.toastService.success('Aktion erfolgreich ausgeführt');
    }
  }

  private extractActionExecution(payload: PipelineActionExecution): {
    action: PipelineAction;
    taskNo?: number;
    startTask?: number;
    endTask?: number;
    trigger: 'rerun' | 'start' | 'unknown';
  } {
    if (this.isStructuredActionExecution(payload)) {
      return {
        action: payload.action,
        taskNo: payload.taskNo,
        startTask: this.getStartTask(payload),
        endTask: this.getEndTask(payload),
        trigger: payload.trigger || 'unknown',
      };
    }

    return {
      action: payload,
      trigger: 'unknown',
    };
  }

  private isStructuredActionExecution(
    payload: PipelineActionExecution,
  ): payload is TimelineActionExecution | DebugTaskActionExecution {
    return (
      typeof payload === 'object' &&
      payload !== null &&
      'action' in payload &&
      typeof payload.action === 'object' &&
      payload.action !== null
    );
  }

  private getStartTask(
    payload: TimelineActionExecution | DebugTaskActionExecution,
  ): number | undefined {
    return 'startTask' in payload ? payload.startTask : undefined;
  }

  private getEndTask(
    payload: TimelineActionExecution | DebugTaskActionExecution,
  ): number | undefined {
    return 'endTask' in payload ? payload.endTask : undefined;
  }

  private async executePipelineAction(
    payload: PipelineActionExecution,
  ): Promise<void> {
    const { action, taskNo, startTask, endTask, trigger } =
      this.extractActionExecution(payload);
    const analysis = this.analysis;

    if (!analysis) {
      this.toastService.error('Analyse ist noch nicht geladen');
      return;
    }

    const orgId = this.organizationId || analysis.organizationId || null;
    if (!orgId) {
      this.toastService.error(
        'Organisation konnte nicht bestimmt werden. Bitte Seite neu laden.',
      );
      return;
    }

    if (!action.hasPermission) {
      this.toastService.error('Keine Berechtigung für diese Aktion');
      return;
    }

    if (action.disabled) {
      this.toastService.warning('Aktion ist aktuell nicht ausführbar');
      return;
    }

    const actionName = action.action.replaceAll('_', '-');

    if (actionName === 'cancel-analysis') {
      await this.analysisService.cancelAnalysis(orgId, analysis.analysisId);
      return;
    }

    const taskExecution = this.resolveTaskExecutionRequest({
      actionName,
      trigger,
      taskNo,
      startTask,
      endTask,
    });

    if (taskExecution) {
      await this.executeTaskRange(orgId, analysis.analysisId, taskExecution);
      return;
    }

    // Redo/reset: reset, then geocut only — see runRedoAnalysis() for why the
    // rest of the pipeline waits for the file/template selection.
    if (actionName === 'redo-analysis' || actionName === 'reset-analysis') {
      await this.analysisService.redoAnalysis(orgId, analysis.analysisId);
      await this.analysisService.executeAnalysisTasks(
        orgId,
        analysis.analysisId,
        {
          start_task: FIRST_PIPELINE_TASK,
          end_task: FIRST_PIPELINE_TASK,
          notes: 'Geocut after reset',
        },
      );
      return;
    }

    if (actionName === 'update-status' && action.targetStatus) {
      await this.analysisService.updateAnalysisStatus(
        orgId,
        analysis.analysisId,
        action.targetStatus,
      );
      return;
    }

    await this.analysisService.syncAnalysisStatus(orgId, analysis.analysisId);
  }

  private resolveTaskExecutionRequest(params: {
    actionName: string;
    trigger: 'rerun' | 'start' | 'unknown';
    taskNo?: number;
    startTask?: number;
    endTask?: number;
  }): TaskExecutionRequest | null {
    const { actionName, trigger, taskNo, startTask, endTask } = params;

    const isAnalysisExecution = actionName === 'start-analysis';
    const isPlanningExecution = actionName === 'start-planning';
    const isTaskExecutionAction = isAnalysisExecution || isPlanningExecution;

    if (
      isTaskExecutionAction &&
      trigger === 'rerun' &&
      this.hasTaskWindow(startTask, endTask)
    ) {
      return {
        startTask: Number(startTask),
        endTask: Number(endTask),
        notes: `Stage rerun from UI (${Number(startTask)}-${Number(endTask)})`,
      };
    }

    if (
      trigger === 'rerun' &&
      this.hasTaskNo(taskNo) &&
      [
        'start-analysis',
        'start-planning',
        'redo-analysis',
        'reset-analysis',
      ].includes(actionName)
    ) {
      return {
        startTask: Number(taskNo),
        endTask: Number(taskNo),
        notes: `Task retry from UI (${actionName})`,
      };
    }

    if (
      isTaskExecutionAction &&
      trigger === 'start' &&
      this.hasTaskWindow(startTask, endTask)
    ) {
      return {
        startTask: Number(startTask),
        endTask: Number(endTask),
      };
    }

    if (
      isTaskExecutionAction &&
      trigger === 'start' &&
      this.hasTaskNo(taskNo)
    ) {
      return {
        startTask: Number(taskNo),
        endTask: Number(taskNo),
        notes: isAnalysisExecution
          ? 'Single task start from progress details'
          : `Single task execution from UI (${actionName})`,
      };
    }

    if (isAnalysisExecution) {
      return { startTask: FIRST_PIPELINE_TASK, endTask: LAST_PIPELINE_TASK };
    }

    if (isPlanningExecution) {
      return { startTask: 4, endTask: 5 };
    }

    return null;
  }

  private hasTaskWindow(startTask?: number, endTask?: number): boolean {
    return Number.isFinite(startTask) && Number.isFinite(endTask);
  }

  private hasTaskNo(taskNo?: number): boolean {
    return Number.isFinite(taskNo) && !!taskNo;
  }

  private async executeTaskRange(
    organizationId: string,
    analysisId: string,
    request: TaskExecutionRequest,
  ): Promise<void> {
    try {
      await this.analysisService.executeAnalysisTasks(organizationId, analysisId, {
        start_task: request.startTask,
        end_task: request.endTask,
        notes: request.notes,
      });
    } catch (error: unknown) {
      const message = (error as Error)?.message ?? '';
      if (!message.includes('currently being processed')) throw error;

      // Analysis stuck in processing — cancel the running Celery task and restart.
      await this.analysisService.executeAnalysisTasks(organizationId, analysisId, {
        start_task: request.startTask,
        end_task: request.endTask,
        notes: request.notes,
        force: true,
      });
    }
  }

  private async runRedoAnalysis(): Promise<void> {
    const analysis = this.analysis;
    const orgId = this.organizationId || analysis?.organizationId;

    if (!analysis || !orgId) {
      this.toastService.error('Erforderliche Daten fehlen');
      return;
    }

    this.isPipelineActionRunning.set(true);
    try {
      await this.analysisService.redoAnalysis(orgId, analysis.analysisId);

      // Run geocut only. The reset wiped the pipeline state, so the files
      // detection consumes don't exist yet — asking for one here would offer
      // stale files (and the dialog's geocut guard would block confirm anyway).
      // "Fortsetzen" asks for file and template once geocut has produced them.
      await this.executeTaskRange(orgId, analysis.analysisId, {
        startTask: FIRST_PIPELINE_TASK,
        endTask: FIRST_PIPELINE_TASK,
        notes: 'Geocut after reset',
      });

      this.ensureRealtimeStream();
      this.reload();
    } catch (error) {
      debugLog('[AnalysisWorkflowFacade] Failed to redo analysis', error);
      this.toastService.error('Analyse-Reset fehlgeschlagen');
    } finally {
      this.isPipelineActionRunning.set(false);
    }
  }
}
